import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, takeUntil } from 'rxjs/operators';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { WeissmanDateFormatPipe } from '../../../UI-Lib/Pipes';
import { WeissmanDatetimeComponent } from '../../../UI-Lib/Weiss-Datepicker/weissman-datetime.component';
import { InvoiceService } from '../invoice.service';
import { ContractsInvoicesService } from '../../../Common/Contracts-Invoices/contract-invoices.service';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';
import { TaskService } from '../../../Task/task.service.upgrade';
import { LaunchTaskModalService } from '../../../Task/launchTaskModal.service';
import { CreateInvoiceModalLaunchService } from '../../../Common/Invoice/create-invoice-modal-launch.service';
import {
    MessageModalButtons,
    MessageModalDisplayMode,
    MessageModalService,
} from '../../../UI-Lib/Message-Box/messageModal.service';
import { Invoice } from '../invoice.model';
import { ContractServiceType, EntityType, RyanInstanceId } from '../../../constants.new';
import { NavigationService } from '../../../Layout/navigation.service';
import { BreadCrumb, BreadCrumbsComponent } from '../../../UI-Lib/Bread-Crumb/breadCrumbs.component';
import { UIRouter } from '@uirouter/angular';
import { CompanyService } from '../../../Entity/Company/company.service';

import moment from 'moment';
import { RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { UserInstanceService } from '../../../User/userInstance.service';
import { AceLoaderComponent } from '../../../UI-Lib/ACE/Loader/loader.component';

@Component({
    selector: 'invoice-page',
    templateUrl: './invoicesPage.component.html',
    styleUrls: ['./invoicesPage.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        WeissmanDateFormatPipe,
        WeissmanDatetimeComponent,
        BreadCrumbsComponent,
        AceLoaderComponent,
    ],
})
export class InvoicesPageComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _invoiceService: InvoiceService,
        private readonly _contractsInvoicesService: ContractsInvoicesService,
        private readonly _messageModalService: MessageModalService,
        private readonly _navigationServiceHandler: UpgradeNavigationServiceHandler,
        private readonly _taskService: TaskService,
        private readonly _launchTaskModalService: LaunchTaskModalService,
        private readonly _createInvoiceModalLaunchService: CreateInvoiceModalLaunchService,
        private readonly _companyService: CompanyService,
        private readonly _toastr: ToastrService,
        private readonly _navigationService: NavigationService,
        private readonly _restrictService: RestrictService,
        private readonly _userInstanceService: UserInstanceService,
        private readonly _router: UIRouter,
    ) {}

    @Input() allowEdit: boolean;

    breadcrumbs: BreadCrumb[];

    editLock: boolean;
    isLoading: boolean;
    showBody: boolean;
    invoices: Invoice[];
    companyId: number;
    siteId: number;
    fromDate: Date;
    toDate: Date;
    parcelsReadyForInvoiceCount: number;
    sitesReadyForInvoiceCount: number;
    creatingAdminInvoices: boolean;
    annualYear: number;
    years: number[];
    ContractServiceType = ContractServiceType;
    contractTermsUpdateSubscription: Subscription;
    showSiteColumn: boolean = false;

    private _destroy$: Subject<void> = new Subject();

    async ngOnInit(): Promise<void> {
        const { companyId, siteId } = this._router.globals.params;
        this.companyId = +companyId;
        this.siteId = +siteId || null;

        const hasViewPermission = this._restrictService.isInRole(Roles.RYANPRIVATEITEMSVIEW);
        const hasEditPermission = this._restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT);

        // getCompany checks the cache first, so no extra call if already loaded
        const company = await this._companyService.getCompany(this.companyId);

        if (
            !(
                (hasViewPermission || hasEditPermission) &&
                this._userInstanceService.getSelectedInstance().instanceId === RyanInstanceId
            )
        ) {
            this._router.stateService.go('company', { companyId: this.companyId });
        }

        this.breadcrumbs = [
            {
                name: company.companyName,
                target: 'company',
                options: { companyId: this.companyId },
            },
        ];

        const dates = this._invoiceService.getDateRangeFromCache(this.companyId, this.siteId);

        if (this.siteId) {
            this.fromDate = dates?.fromDate || null;
            this.toDate = dates?.toDate || null;

            this.contractTermsUpdateSubscription = this._contractsInvoicesService.contractTermsUpdateObservable
                .pipe(
                    takeUntil(this._destroy$),
                    filter((event) => event.entityId == this.siteId && event.entityType == EntityType.Site),
                )
                .subscribe(() => this.updateSiteReadyCounts());
        } else {
            this.fromDate =
                dates?.fromDate ||
                WeissmanDatetimeComponent.getMidnight(moment().subtract(90, 'day').toDate(), false, 'Central');

            //WK-4406 - changed this to null to prevent an issue where it looks like company page
            //isn't seeing site invoices due to due date range issues
            this.toDate = dates?.toDate || null;

            this.contractTermsUpdateSubscription = this._contractsInvoicesService.contractTermsUpdateObservable
                .pipe(
                    takeUntil(this._destroy$),
                    filter((event) => event.entityId == this.companyId && event.entityType == EntityType.Company),
                )
                .subscribe(() => this.updateCompanyReadyCounts());
        }

        this.years = [];
        this.annualYear = new Date().getFullYear();

        for (let i = this.annualYear + 5; i > this.annualYear - 11; i--) {
            this.years.push(i);
        }

        this.allowEdit = hasEditPermission;
        this._navigationService.globalEditMode$.pipe(takeUntil(this._destroy$)).subscribe((editMode) => {
            this.editLock = editMode;
        });

        await this.loadPanel();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async loadPanel(): Promise<void> {
        this.isLoading = true;

        const actions = [
            this._invoiceService.getInvoices(this.companyId, this.siteId, this.fromDate, this.toDate).then((result) => {
                this.invoices = result.map((x) => {
                    x.contractServiceName = this._invoiceService.contractServiceName(x.contractServiceType);
                    return x;
                });

                this.showSiteColumn = this.invoices.some((x) => x.siteName);
            }),
        ];

        if (this.siteId) {
            actions.push(this.updateSiteReadyCounts());
        } else {
            actions.push(this.updateCompanyReadyCounts());
        }

        try {
            await Promise.all(actions);
        } finally {
            this.isLoading = false;
        }
    }

    cancelCreateAdminInvoices(): void {
        this.creatingAdminInvoices = false;
        this._navigationService.disableNavWarning();
    }

    startCreateAdminInvoices(): void {
        this.creatingAdminInvoices = true;
        this._navigationService.enableNavWarning(
            'You are in the process of creating invoices. Are you sure you want to leave this page?',
        );
    }

    async createAdminInvoices(): Promise<void> {
        this.isLoading = true;
        const preCheckResult = await this._invoiceService.bulkInvoicePreCheck(this.companyId, this.annualYear);
        this.isLoading = false;

        if (preCheckResult.siteCount < 1) {
            this._toastr.warning(
                'No sites can be invoiced for that tax year (most likely there are no matching contract terms available)',
            );
        } else {
            let title: string = undefined;
            let message = `Are you sure you want to create ${preCheckResult.siteCount} invoice(s)?`;
            if (preCheckResult.existingInvoiceCount > 0) {
                title = 'Existing admin invoices detected';
                message = `WARNING: There appear to be ${preCheckResult.existingInvoiceCount} existing admin invoice(s). ${message}`;
            }
            try {
                await this._messageModalService.open({
                    message: message,
                    buttons: MessageModalButtons.YesNo,
                    title: title,
                    displayMode: MessageModalDisplayMode.SingleMessage,
                });
                this.creatingAdminInvoices = false;
                this._navigationService.disableNavWarning();

                this.isLoading = true;
                const result = await this._invoiceService.createCompanyAdminInvoices(this.companyId, this.annualYear);
                this.handleInvoiceCreate(result, false);
            } catch (e) {
                this.loadPanel();
            } finally {
                this.isLoading = false;
            }
        }
    }

    async createInvoices(): Promise<void> {
        try {
            await this._messageModalService.open({
                title: 'WARNING',
                message:
                    'Selected Invoice Appeal Task(s) will be marked complete and their related appeal savings converted into saved contingency invoices ready to process as drafts. Proceed?',
                buttons: MessageModalButtons.OKCancel,
                displayMode: MessageModalDisplayMode.SingleMessage,
            });
            try {
                const result = await this._invoiceService.createInvoices(this.siteId);
                this.handleInvoiceCreate(result, true);
            } catch {
                await this.loadPanel();
            }
        } catch (e) {
            return e;
        }
    }

    createAdHocInvoice(): void {
        this._createInvoiceModalLaunchService.openCreateInvoiceModal(
            (invoiceId: number) => {
                // Redirect to new invoice on create. If we change our minds and want it to just reload the invoice
                // panel afterward, we could remove the nav code and replace it with this:
                //this.toastr.success('Ad Hoc Invoice Created');
                //this.loadPanel();
                if (this.siteId) {
                    this._navigationServiceHandler.go('siteInvoice', {
                        companyID: this.companyId,
                        siteID: this.siteId,
                        invoiceID: invoiceId,
                    });
                } else {
                    this._navigationServiceHandler.go('companyInvoice', {
                        companyID: this.companyId,
                        invoiceID: invoiceId,
                    });
                }
            },
            this.companyId,
            this.siteId,
        );
    }

    rowSelected(invoice: Invoice): void {
        this._invoiceService.updateDateRangeCache(this.companyId, this.siteId, this.fromDate, this.toDate);
        if (this.siteId) {
            this._navigationServiceHandler.go('siteInvoice', {
                companyID: this.companyId,
                siteID: this.siteId,
                invoiceID: invoice.invoiceId,
            });
        } else {
            this._navigationServiceHandler.go('companyInvoice', {
                companyID: this.companyId,
                invoiceID: invoice.invoiceId,
            });
        }
    }

    async launchTaskModal(event, invoice: Invoice): Promise<void> {
        event.stopPropagation();
        await this._launchTaskModalService.launchTaskModal(invoice.invoiceId, EntityType.Invoice, true);
    }

    private async updateSiteReadyCounts(): Promise<void> {
        this.parcelsReadyForInvoiceCount = null;
        this.parcelsReadyForInvoiceCount = await this._invoiceService.getReadyInvoiceAppealSavingTasksCount(
            this.siteId,
        );
    }

    private async updateCompanyReadyCounts(): Promise<void> {
        this.sitesReadyForInvoiceCount = null;
        this.sitesReadyForInvoiceCount = await this._invoiceService.getReadyAdminFeeInvoiceCount(this.companyId);
    }

    private handleInvoiceCreate(result, isTask: boolean): void {
        const successfulResults = result.operationResult.filter((x) => !x.errorMessage && !x.warningMessage);
        const errorResults = result.operationResult.filter((x) => x.errorMessage || x.warningMessage);

        if (errorResults.length < 1) {
            if (isTask) {
                this._toastr.success(`Successfully processed ${successfulResults.length} tasks`);
            } else {
                this._toastr.success(`Successfully created ${successfulResults.length} invoices`);
            }
        } else {
            const warnings: string[] = Array.from(
                new Set(errorResults.filter((error) => !!error.warningMessage).map((error) => error.warningMessage)),
            );

            let errors: string[] = Array.from(
                new Set(errorResults.filter((error) => !error.warningMessage).map((error) => error.errorMessage)),
            );

            let showError = true;
            // If we got error messages but no user-friendly warning messages, don't bother with the
            // error-notification modal; the toast will be sufficient for that case
            if (warnings.some((w) => !!w)) {
                // Do not show the original message for an error; any user-readable errors should come back
                // as warnings
                if (errors.some((e) => !!e)) {
                    errors = ['An unexpected error has occurred'];
                }
                this._taskService.showErrorNotificationModal(
                    errors,
                    warnings,
                    'The following error(s) were encountered attempting to create invoices',
                );
                showError = false;
            }

            if (successfulResults && successfulResults.length > 0) {
                if (isTask) {
                    this._toastr.warning(
                        `Only some task(s) successfully processed; ${successfulResults.length} succeeded and ${errorResults.length} failed`,
                    );
                } else {
                    this._toastr.warning(
                        `Only some invoices(s) successfully created; ${successfulResults.length} succeeded and ${errorResults.length} failed`,
                    );
                }
                showError = false;
            }

            if (showError) {
                this._toastr.error('An unexpected error has occurred');
            }
        }

        this.loadPanel();
    }
}
